import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { useNavigate } from 'react-router-dom'

import { ChevronsUpDown, HelpCircle, LogOut, Settings, Users } from 'lucide-react'

import { hasRole } from '@/lib/roles'
import { capitalizeName } from '@/lib/utils'

import { useAuthStore } from '@/stores/auth'

import { paths } from '@/config/paths'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar'

import { SmartAvatar } from '../../../components/ui-custom/SmartAvatar'

export function NavUser() {
  const { isMobile } = useSidebar()
  const { t } = useTranslation(['common'])
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()

  const handleLogout = async () => {
    await logout()
  }

  // Role-based menu items
  const menuItems = useMemo(() => {
    const isMember = hasRole(user, 'member')
    const isStaff = hasRole(user, ['super_admin', 'gym_manager', 'assistant', 'coach'])

    const items = [
      {
        key: 'account',
        icon: <Settings size={14} className='opacity-60' aria-hidden='true' />,
        label: t('userDropdown.menu.account', { ns: 'common', defaultValue: 'Account settings' }),
        onSelect: () => navigate(paths.dashboard.profile.root.getHref()),
        roles: ['member', 'super_admin', 'gym_manager', 'assistant', 'coach'],
      },
    ]

    // Member-specific menu items
    if (isMember) {
      items.push({
        key: 'my-bookings',
        icon: <Users size={14} className='opacity-60' aria-hidden='true' />,
        label: t('userDropdown.menu.myBookings', { ns: 'common', defaultValue: 'My Bookings' }),
        onSelect: () => navigate(paths.dashboard.member.myBookings.getHref()),
        roles: ['member'],
      })
    }

    // Staff-specific menu items
    if (isStaff) {
      items.push({
        key: 'bookings',
        icon: <Users size={14} className='opacity-60' aria-hidden='true' />,
        label: t('userDropdown.menu.bookings', { ns: 'common', defaultValue: 'Manage Bookings' }),
        onSelect: () => navigate(paths.dashboard.bookings.root.getHref()),
        roles: ['super_admin', 'gym_manager', 'assistant', 'coach'],
      })
    }

    // Common menu items
    items.push({
      key: 'help',
      icon: <HelpCircle size={14} className='opacity-60' aria-hidden='true' />,
      label: t('userDropdown.menu.help', { ns: 'common', defaultValue: 'Help' }),
      onSelect: () => navigate(paths.dashboard.support.root.getHref()),
      roles: ['member', 'super_admin', 'gym_manager', 'assistant', 'coach'],
    })

    return items
  }, [user, t, navigate])

  const logoutItem = {
    key: 'logout',
    icon: <LogOut size={14} className='opacity-60' aria-hidden='true' />,
    label: t('userDropdown.menu.logOut', { ns: 'common', defaultValue: 'Log Out' }),
    onClick: (e: React.SyntheticEvent) => {
      e.preventDefault()
      handleLogout()
    },
  }
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size='lg'
              className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground cursor-pointer'
            >
              <SmartAvatar
                size='sm'
                name={user?.full_name ?? ''}
                alt={user?.full_name ?? ''}
                src={user?.profile_photo ?? ''}
                className='shadow-md'
              />
              <div className='grid flex-1 text-left text-sm leading-tight'>
                <span className='truncate font-medium'>{user?.full_name ? capitalizeName(user?.full_name) : ''}</span>
                <span className='truncate text-xs'>{user?.email ?? ''}</span>
              </div>
              <ChevronsUpDown className='ml-auto size-4' />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg'
            side={isMobile ? 'bottom' : 'right'}
            align='end'
            sideOffset={4}
          >
            <DropdownMenuLabel className='p-0 font-normal'>
              <div className='flex items-center gap-2 px-1 py-1.5 text-left text-sm'>
                <SmartAvatar
                  size='sm'
                  name={user?.full_name ?? ''}
                  alt={user?.full_name ?? ''}
                  src={user?.profile_photo ?? ''}
                  className='shadow-md'
                />
                <div className='grid flex-1 text-left text-sm leading-tight'>
                  <span className='truncate font-medium'>{user?.full_name ? capitalizeName(user?.full_name) : ''}</span>
                  <span className='truncate text-xs'>{user?.email ?? ''}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {menuItems.map(({ key, icon, label, onSelect }) => (
                <DropdownMenuItem key={key} onSelect={onSelect} className='cursor-pointer'>
                  {icon}
                  <span>{label}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logoutItem.onClick} className='cursor-pointer'>
              {logoutItem.icon}
              <span>{logoutItem.label}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
